import React, { useState, useEffect } from "react";
import {
  getPaymentHistory,
  requestPayment,
  profileData,
  getBalance,
} from "../../api/payment";
import Card from "../shared/Card";
import Button from "../shared/Button";
import Swal from "sweetalert2";
import { PuffLoader } from "react-spinners";
import { getSales } from "../../api/referral";

const PAYMENTS_ENABLED = false;

// simular numero de referidos
// const getUserReferrals = async () => {
//   await new Promise((resolve) => setTimeout(resolve, 300));
//   return 20;
// };

const PaymentHistory = () => {
  const REQUIRED_REFERRALS = 20;

  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userProfileComplete, setUserProfileComplete] = useState(false);
  const [profile, setProfile] = useState(null);
  const [userReferrals, setUserReferrals] = useState(0);

  const loadData = async () => {
    try {
      const [paymentsData, balanceData, referralsSales] = await Promise.all([
        getPaymentHistory(),
        getBalance(),
        getSales(),
      ]);

      setPayments(paymentsData);
      setBalance(balanceData);
      setUserReferrals(referralsSales);

      try {
        const userProfile = await profileData();
        setProfile(userProfile);

        const isProfileComplete = validateUserProfile(userProfile);
        setUserProfileComplete(isProfileComplete);
      } catch (profileError) {
        console.error("Error al cargar perfil de usuario:", profileError);
        setUserProfileComplete(false);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar el historial de pagos",
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para validar si el perfil está completo
  const validateUserProfile = (userProfile) => {
    if (!userProfile) return false;

    const personalInfoComplete =
      userProfile.first_name &&
      userProfile.first_name.trim() !== "" &&
      userProfile.last_name &&
      userProfile.last_name.trim() !== "" &&
      userProfile.identification &&
      userProfile.identification.number &&
      userProfile.identification.number.trim() !== "";

    const paymentInfoComplete =
      userProfile.account &&
      userProfile.account.number &&
      userProfile.account.number.trim() !== "" &&
      userProfile.account.bank &&
      userProfile.account.bank.trim() !== "";

    return personalInfoComplete && paymentInfoComplete;
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRequestPayment = async () => {
    if (!userProfileComplete) {
      Swal.fire({
        icon: "warning",
        title: "Información incompleta",
        text: "Para solicitar pagos debes completar tu información personal y bancaria en tu perfil.",
        footer:
          '<a href="/profile" id="go-to-profile" style="color: #009ee3;">Ir a Información Personal</a>',
      });
      return;
    }

    if (userReferrals < REQUIRED_REFERRALS) {
      Swal.fire({
        icon: "warning",
        title: "Referidos insuficientes",
        text: `Necesitas al menos ${REQUIRED_REFERRALS} referidos diferentes que hayan generado al menos una venta en la plataforma para solicitar pagos. Actualmente tienes ${userReferrals}.`,
      });
      return;
    }

    const { value: amount } = await Swal.fire({
      title: "Solicitar Pago",
      input: "number",
      inputLabel: "Monto a solicitar",
      inputPlaceholder: "Ingrese el monto",
      inputValue: balance,
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return "Debe ingresar un monto";
        }
        if (parseFloat(value) <= 0) {
          return "El monto debe ser mayor que cero";
        }
        if (parseFloat(value) > balance) {
          return "El monto no puede ser mayor al balance disponible";
        }
      },
    });

    if (amount) {
      try {
        const response = await requestPayment(parseFloat(amount));

        if (response) {
          await loadData();
          Swal.fire({
            icon: "success",
            title: "¡Solicitud enviada!",
            text: "Tu solicitud de pago ha sido registrada",
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Error al solicitar el pago",
        });
      }
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "badge badge--warning";
      case "processed":
        return "badge badge--success";
      case "rejected":
        return "badge badge--error";
      default:
        return "badge";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="loader-container">
        <PuffLoader
          color="#009ee3"
          loading={true}
          size={60}
          margin={2}
          speedMultiplier={4}
        />
      </div>
    );
  }

  const hasEnoughReferrals = userReferrals >= REQUIRED_REFERRALS;

  return (
    <div className="payment-history">
      <Card className="balance-card">
        <div className="balance-card__content">
          <div className="balance-card__info">
            <h3>Balance Disponible COP</h3>
            <p className="balance-card__amount">{balance}</p>
            {!PAYMENTS_ENABLED && (
              <p className="balance-card__support-message">
                Para solicitar el retiro de tu balance, debes comunicarte con
                nuestro equipo de soporte. Ellos procesarán tu solicitud
                manualmente.
              </p>
            )}
          </div>
          {PAYMENTS_ENABLED && (
            <div className="request-payment-section">
              <Button
                onClick={handleRequestPayment}
                // disabled={!hasEnoughReferrals}
                className={
                  !userProfileComplete || !hasEnoughReferrals
                    ? "button--with-tooltip"
                    : ""
                }
                title={
                  !userProfileComplete
                    ? "Completa tu perfil para solicitar pagos"
                    : !hasEnoughReferrals
                      ? `Necesitas ${REQUIRED_REFERRALS} referidos para solicitar pagos (Actualmente: ${userReferrals})`
                      : ""
                }
              >
                Solicitar Pago
              </Button>
            </div>
          )}
        </div>
      </Card>

      {PAYMENTS_ENABLED && (
        <Card title="Historial de Pagos" className="payments-card">
          {payments.length === 0 ? (
            <p className="payments-card__empty">No hay pagos registrados</p>
          ) : (
            <div className="payments-list">
              {payments.map((payment) => (
                <div key={payment.id} className="payment-item">
                  <div className="payment-item__info">
                    <p className="payment-item__amount">COP {payment.amount}</p>
                    <p className="payment-item__date">
                      {formatDate(payment.created_at)}
                    </p>
                  </div>
                  <span className={getStatusBadgeClass(payment.status)}>
                    {payment.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default PaymentHistory;
